import { useState, useCallback, useMemo, useRef } from 'react';
import {
    Button,
    TextField,
    Card,
    CardContent,
    Box,
    Typography,
    Container,
    Stack,
    LinearProgress,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import SpeedIcon from '@mui/icons-material/Speed';
import GroupsIcon from '@mui/icons-material/Groups';
import GppGoodIcon from '@mui/icons-material/GppGood';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import SendIcon from '@mui/icons-material/Send';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReCAPTCHA, { ReCAPTCHARef } from 'react-google-recaptcha';
import { ReCAPTCHA_SITE_KEY } from '@modules/consts';
import { RequestAccessForm, RequestState } from '@synergetx/puppet-interview-types';
import { submitAccessRequest } from '@modules/api';
import Navbar from '@components/Navbar';
import ValidatedTextField from '@components/ValidatedTextField';
import FormErrorSummary from '@components/FormErrorSummary';
import Footer from '@components/Footer';

const requestValidationSchema = Yup.object<RequestAccessForm>({
    name: Yup.string().required('Your name is required').min(1, 'Your name is required'),
    email: Yup.string().required('Email is required').email('Please enter a valid email'),
    organization: Yup.string()
        .required('An organization is required')
        .min(1, 'An organization is required'),
});

const RequestAccess = () => {
    const theme = useTheme();
    const recaptchaRef = useRef<ReCAPTCHARef>(null);
    const [submitted, setSubmitted] = useState(false);

    const initialValues = useMemo(
        (): RequestAccessForm => ({
            email: '',
            name: '',
            organization: '',
            status: RequestState.REQUESTED,
        }),
        [],
    );

    const onSubmit = useCallback(async (values: RequestAccessForm) => {
        try {
            if (!recaptchaRef.current) {
                toast.error('Error with reCAPTCHA. Please check your connection and try again.');
                return;
            }

            const token = ReCAPTCHA_SITE_KEY ? await recaptchaRef.current.executeAsync() : '';
            const requestData = { ...values, token };

            await toast.promise(submitAccessRequest(requestData), {
                pending: 'Requesting access...',
                success: 'Request submitted successfully',
                error: 'Error requesting access. Please try again later.',
            });

            setSubmitted(true);
        } finally {
            recaptchaRef.current?.reset();
        }
    }, []);

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Navbar />
            <Box
                sx={{
                    flex: 1,
                    background: 'linear-gradient(180deg, rgba(13, 6, 56, 0.04) 0%, #ffffff 60%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: { xs: 4, md: 6 },
                    pb: { md: 10 },
                }}
            >
                <Container maxWidth="md">
                    {!submitted && (
                        <>
                            <Box sx={{ textAlign: 'center', mb: 8, width: '100%' }}>
                                <Typography
                                    variant="h2"
                                    sx={{
                                        fontSize: { xs: '2rem', md: '3rem', lg: '3.5rem' },
                                        fontWeight: 'bold',
                                        mb: 4,
                                        // Solid, and from the theme rather than a fourth
                                        // hardcoded hex. The indigo-to-magenta gradient this
                                        // replaced could not be contrast-audited, and
                                        // WebkitTextFillColor: transparent meant the headline
                                        // vanished entirely if the background failed to paint.
                                        color: 'secondary.main',
                                    }}
                                >
                                    Get Early Access to Puppet Interview
                                </Typography>

                                <Box
                                    sx={{
                                        display: { xs: 'none', md: 'grid' },
                                        gridTemplateColumns: { md: 'repeat(3, 1fr)' },
                                        gap: 3,
                                        mb: 8,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            p: 2,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                backgroundColor: alpha(
                                                    theme.palette.primary.main,
                                                    0.1,
                                                ),
                                                p: 2,
                                                borderRadius: '50%',
                                                mb: 2,
                                                width: 72,
                                                height: 72,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <SpeedIcon
                                                sx={{ fontSize: 40, color: 'primary.main' }}
                                            />
                                        </Box>
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                            sx={{
                                                fontWeight: '600',
                                            }}
                                        >
                                            Save 94% Time
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'text.secondary',
                                                textAlign: 'center',
                                            }}
                                        >
                                            Reduce survey administration time dramatically with
                                            automated puppet interviews
                                        </Typography>
                                    </Box>

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            p: 2,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                backgroundColor: alpha(
                                                    theme.palette.primary.main,
                                                    0.1,
                                                ),
                                                p: 2,
                                                borderRadius: '50%',
                                                mb: 2,
                                                width: 72,
                                                height: 72,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <GroupsIcon
                                                sx={{ fontSize: 40, color: 'primary.main' }}
                                            />
                                        </Box>
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                            sx={{
                                                fontWeight: '600',
                                            }}
                                        >
                                            500+ Researchers
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'text.secondary',
                                                textAlign: 'center',
                                            }}
                                        >
                                            Join the growing community of educators and researchers
                                            worldwide
                                        </Typography>
                                    </Box>

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            p: 2,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                backgroundColor: alpha(
                                                    theme.palette.primary.main,
                                                    0.1,
                                                ),
                                                p: 2,
                                                borderRadius: '50%',
                                                mb: 2,
                                                width: 72,
                                                height: 72,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <GppGoodIcon
                                                sx={{ fontSize: 40, color: 'primary.main' }}
                                            />
                                        </Box>
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                            sx={{
                                                fontWeight: '600',
                                            }}
                                        >
                                            COPPA Compliant
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'text.secondary',
                                                textAlign: 'center',
                                            }}
                                        >
                                            Enterprise-grade security that protects children's data
                                            and privacy
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            <Card sx={{ maxWidth: 720, mx: 'auto', boxShadow: 4 }}>
                                <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                                        <Typography
                                            variant="h4"
                                            gutterBottom
                                            sx={{
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            Request Access
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: 'text.secondary',
                                            }}
                                        >
                                            Fill out the form below and we'll get you started with a
                                            personalized demo and trial access.
                                        </Typography>
                                    </Box>

                                    <Formik
                                        enableReinitialize
                                        initialValues={initialValues}
                                        onSubmit={onSubmit}
                                        validateOnChange={true}
                                        validationSchema={requestValidationSchema}
                                    >
                                        {({
                                            errors,
                                            touched,
                                            values,
                                            isSubmitting,
                                            submitCount,
                                        }) => (
                                            // noValidate: the fields keep `required` for assistive
                                            // tech, but without this the browser's own constraint
                                            // validation blocks submit and shows its native
                                            // bubble, so Formik never runs and the summary below
                                            // never appears.
                                            <Form noValidate aria-busy={isSubmitting}>
                                                {isSubmitting && <LinearProgress sx={{ mb: 2 }} />}
                                                <Stack spacing={3}>
                                                    <FormErrorSummary
                                                        errors={errors}
                                                        submitCount={submitCount}
                                                    />
                                                    <Grid container spacing={2}>
                                                        <Grid size={{ xs: 12, md: 6 }}>
                                                            <ValidatedTextField
                                                                name="name"
                                                                label="Full Name"
                                                                placeholder="Dr. Jane Smith"
                                                                required
                                                                disabled={isSubmitting}
                                                                touched={touched.name}
                                                                error={errors.name}
                                                                value={values.name}
                                                            />
                                                        </Grid>

                                                        <Grid size={{ xs: 12, md: 6 }}>
                                                            <ValidatedTextField
                                                                name="email"
                                                                label="Email Address"
                                                                placeholder="jane.smith@university.edu"
                                                                type="email"
                                                                required
                                                                disabled={isSubmitting}
                                                                touched={touched.email}
                                                                error={errors.email}
                                                                value={values.email}
                                                            />
                                                        </Grid>
                                                    </Grid>

                                                    <Grid container spacing={2}>
                                                        <Grid size={{ xs: 12, md: 6 }}>
                                                            <ValidatedTextField
                                                                name="organization"
                                                                label="Organization"
                                                                placeholder="University of Example"
                                                                required
                                                                disabled={isSubmitting}
                                                                touched={touched.organization}
                                                                error={errors.organization}
                                                                value={values.organization}
                                                            />
                                                        </Grid>

                                                        <Grid size={{ xs: 12, md: 6 }}>
                                                            <TextField
                                                                label="Research Area/Field of Study"
                                                                placeholder="Child Development, Education, etc."
                                                                variant="outlined"
                                                                fullWidth
                                                            />
                                                        </Grid>
                                                    </Grid>

                                                    <TextField
                                                        label="Tell us about your research goals"
                                                        variant="outlined"
                                                        fullWidth
                                                        multiline
                                                        rows={4}
                                                    />

                                                    <Button
                                                        type="submit"
                                                        variant="contained"
                                                        color="primary"
                                                        size="large"
                                                        fullWidth
                                                        disabled={isSubmitting}
                                                        startIcon={<SendIcon />}
                                                        sx={{ py: 1.5, fontWeight: 600 }}
                                                    >
                                                        Request Access
                                                    </Button>

                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: 'text.secondary',
                                                            textAlign: 'center',
                                                        }}
                                                    >
                                                        <LockOutlinedIcon
                                                            sx={{
                                                                fontSize: 16,
                                                                verticalAlign: 'middle',
                                                                mr: 0.5,
                                                            }}
                                                        />
                                                        Your information is secure and will never be
                                                        shared with third parties
                                                    </Typography>
                                                </Stack>
                                            </Form>
                                        )}
                                    </Formik>
                                </CardContent>
                            </Card>

                            <Box sx={{ mt: 6, textAlign: 'center', width: '100%' }}>
                                <Typography
                                    variant="h4"
                                    gutterBottom
                                    sx={{
                                        fontWeight: 'bold',
                                    }}
                                >
                                    What Happens Next?
                                </Typography>

                                <Grid container spacing={4} sx={{ mt: 6 }}>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    backgroundColor: alpha(
                                                        theme.palette.primary.main,
                                                        0.1,
                                                    ),
                                                    p: 2,
                                                    borderRadius: '50%',
                                                    mb: 2,
                                                    width: 64,
                                                    height: 64,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Typography
                                                    variant="h5"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        color: 'primary.main',
                                                    }}
                                                >
                                                    1
                                                </Typography>
                                            </Box>
                                            <Typography
                                                variant="h6"
                                                gutterBottom
                                                sx={{
                                                    fontWeight: '600',
                                                }}
                                            >
                                                Instant Confirmation
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: 'text.secondary',
                                                }}
                                            >
                                                You'll receive a welcome email with next steps
                                                within minutes
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    backgroundColor: alpha(
                                                        theme.palette.primary.main,
                                                        0.1,
                                                    ),
                                                    p: 2,
                                                    borderRadius: '50%',
                                                    mb: 2,
                                                    width: 64,
                                                    height: 64,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Typography
                                                    variant="h5"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        color: 'primary.main',
                                                    }}
                                                >
                                                    2
                                                </Typography>
                                            </Box>
                                            <Typography
                                                variant="h6"
                                                gutterBottom
                                                sx={{
                                                    fontWeight: '600',
                                                }}
                                            >
                                                Personalized Demo
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: 'text.secondary',
                                                }}
                                            >
                                                Our team will reach out to schedule a customized
                                                demonstration
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    backgroundColor: alpha(
                                                        theme.palette.primary.main,
                                                        0.1,
                                                    ),
                                                    p: 2,
                                                    borderRadius: '50%',
                                                    mb: 2,
                                                    width: 64,
                                                    height: 64,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Typography
                                                    variant="h5"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        color: 'primary.main',
                                                    }}
                                                >
                                                    3
                                                </Typography>
                                            </Box>
                                            <Typography
                                                variant="h6"
                                                gutterBottom
                                                sx={{
                                                    fontWeight: '600',
                                                }}
                                            >
                                                Start Researching
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: 'text.secondary',
                                                }}
                                            >
                                                Begin your first puppet interview project with full
                                                support
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </>
                    )}

                    {submitted && (
                        <Stack
                            sx={{
                                alignItems: 'center',
                                gap: 2,
                                py: 6,
                                width: '100%',
                            }}
                        >
                            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 72 }} />
                            <Typography variant="h5">Success</Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    textAlign: 'center',
                                    maxWidth: 520,
                                }}
                            >
                                Thanks for your request. Please check your email in the coming days
                                for next steps and further access information.
                            </Typography>
                            <Button component={Link} to="/" variant="outlined" sx={{ mt: 4 }}>
                                Return to Home
                            </Button>
                        </Stack>
                    )}
                </Container>
            </Box>
            <Footer />

            {ReCAPTCHA_SITE_KEY ? (
                <ReCAPTCHA
                    ref={recaptchaRef}
                    badge="bottomright"
                    size="invisible"
                    sitekey={ReCAPTCHA_SITE_KEY}
                />
            ) : null}
        </Box>
    );
};

export default RequestAccess;
